import { useState, useMemo, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { reportBreakdown, closeBreakdown, formatDuration, carLabelOf } from '../utils/breakdownService';

const SEVERITIES = [
  { key: 'low', fr: 'Légère', ar: 'خفيف' },
  { key: 'medium', fr: 'Moyenne', ar: 'متوسط' },
  { key: 'high', fr: 'Grave', ar: 'خطير' },
];

export default function BreakdownsPage({ settings = {} }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const t = (fr, ar) => (isAr ? ar : fr);

  const { data: breakdowns, loading, add: addBreakdown, update: updateBreakdown } = useCollection('breakdowns');
  const { data: cars, update: updateCar } = useCollection('cars');

  const [filter, setFilter] = useState('open'); // open | closed | all
  const [search, setSearch] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  // Report form
  const [formCarId, setFormCarId] = useState('');
  const [formComment, setFormComment] = useState('');
  const [formSeverity, setFormSeverity] = useState('medium');

  // Close form
  const [closing, setClosing] = useState(null); // breakdown being closed
  const [closeComment, setCloseComment] = useState('');

  // Live clock for open-repair durations.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const sorted = useMemo(() =>
    [...breakdowns].sort((a, b) => new Date(b.reportedAt || 0) - new Date(a.reportedAt || 0)),
    [breakdowns]);

  const filtered = useMemo(() => sorted.filter(b => {
    if (filter !== 'all' && (b.status || 'open') !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return `${b.carLabel || ''} ${b.plate || ''} ${b.comment || ''}`.toLowerCase().includes(s);
    }
    return true;
  }), [sorted, filter, search]);

  const stats = useMemo(() => {
    const open = breakdowns.filter(b => (b.status || 'open') === 'open');
    const closed = breakdowns.filter(b => b.status === 'closed');
    const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
    const closedThisMonth = closed.filter(b => b.closedAt && new Date(b.closedAt) >= monthStart);
    const durations = closed.map(b => b.durationMin || 0).filter(Boolean);
    const avg = durations.length ? durations.reduce((a, c) => a + c, 0) / durations.length : 0;
    return { openCount: open.length, closedMonth: closedThisMonth.length, avg };
  }, [breakdowns]);

  const openDuration = (b) => formatDuration((nowTick - new Date(b.reportedAt || nowTick)) / 60000, lang);

  // Cars eligible to report (exclude those already under repair with an open record).
  const openCarIds = new Set(breakdowns.filter(b => (b.status || 'open') === 'open').map(b => b.carId));
  const reportableCars = cars.filter(c => !openCarIds.has(c.id));

  const submitReport = async () => {
    const car = cars.find(c => c.id === formCarId);
    if (!car) { notify(t('Sélectionnez un véhicule', 'اختر مركبة')); return; }
    setBusy(true);
    try {
      await reportBreakdown({
        car, comment: formComment, severity: formSeverity,
        settings, lang, addBreakdown, updateCar,
      });
      setShowReport(false); setFormCarId(''); setFormComment(''); setFormSeverity('medium');
      notify(t('✅ Panne signalée — véhicule en réparation', '✅ تم الإبلاغ — المركبة قيد الإصلاح'));
    } catch (e) { notify('❌ ' + e.message); }
    setBusy(false);
  };

  const submitClose = async () => {
    if (!closing) return;
    setBusy(true);
    try {
      await closeBreakdown({
        breakdown: closing, closeComment, settings, lang, updateBreakdown, updateCar,
      });
      setClosing(null); setCloseComment('');
      notify(t('✅ Réparation clôturée — véhicule disponible', '✅ تم إغلاق الإصلاح — المركبة متاحة'));
    } catch (e) { notify('❌ ' + e.message); }
    setBusy(false);
  };

  const exportCsv = () => {
    const head = ['Vehicule', 'Plaque', 'Gravite', 'Statut', 'Signale', 'Cloture', 'Duree(min)', 'Commentaire'];
    const rows = filtered.map(b => [
      b.carLabel, b.plate, b.severity, b.status,
      b.reportedAt ? new Date(b.reportedAt).toLocaleString('fr-FR') : '',
      b.closedAt ? new Date(b.closedAt).toLocaleString('fr-FR') : '',
      b.durationMin || '', (b.comment || '').replace(/[\n;]/g, ' '),
    ]);
    const csv = [head, ...rows].map(r => r.map(c => `"${String(c ?? '')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `pannes_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const sevLabel = (k) => { const s = SEVERITIES.find(x => x.key === k); return s ? t(s.fr, s.ar) : (k || '—'); };
  const sevColor = (k) => k === 'high' ? 'bg-red-100 text-red-700' : k === 'low' ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700';

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            🛠️ {t('Pannes & Réparations', 'الأعطال والإصلاحات')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('Signalez une panne, suivez les réparations et l\'historique par véhicule.',
               'أبلغ عن عطل، تابع الإصلاحات والسجل لكل مركبة.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl font-bold text-sm text-gray-700 dark:text-gray-200">
            ⬇️ {t('Exporter', 'تصدير')}
          </button>
          <button onClick={() => setShowReport(true)} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-sm">
            ➕ {t('Signaler une panne', 'الإبلاغ عن عطل')}
          </button>
        </div>
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-4">
          <div className="text-3xl font-black text-red-600">{stats.openCount}</div>
          <div className="text-xs font-bold text-red-700 dark:text-red-300">{t('En réparation', 'قيد الإصلاح')}</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-4">
          <div className="text-3xl font-black text-green-600">{stats.closedMonth}</div>
          <div className="text-xs font-bold text-green-700 dark:text-green-300">{t('Réparées ce mois', 'تم إصلاحها هذا الشهر')}</div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-4">
          <div className="text-3xl font-black text-blue-600">{stats.avg ? formatDuration(stats.avg, lang) : '—'}</div>
          <div className="text-xs font-bold text-blue-700 dark:text-blue-300">{t('Durée moyenne réparation', 'متوسط مدة الإصلاح')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {[['open', t('En réparation', 'قيد الإصلاح')], ['closed', t('Réparées', 'تم إصلاحها')], ['all', t('Toutes', 'الكل')]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-lg text-sm font-bold ${filter === k ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
            {label}
          </button>
        ))}
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t('Rechercher un véhicule…', 'ابحث عن مركبة…')}
          className="flex-1 min-w-[160px] px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm" />
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-gray-400 py-10">⏳</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-400 py-10 text-sm">{t('Aucune panne.', 'لا يوجد أعطال.')}</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(b => {
            const isOpen = (b.status || 'open') === 'open';
            return (
              <div key={b.id} className={`border rounded-2xl p-4 bg-white dark:bg-gray-800 ${isOpen ? 'border-red-200 dark:border-red-800' : 'border-gray-200 dark:border-gray-700'}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-black text-gray-900 dark:text-white flex items-center gap-2">
                      🚗 {b.carLabel || carLabelOf(cars.find(c => c.id === b.carId) || {})}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${sevColor(b.severity)}`}>{sevLabel(b.severity)}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isOpen ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {isOpen ? t('🔧 En réparation', '🔧 قيد الإصلاح') : t('✅ Réparée', '✅ تم إصلاحها')}
                      </span>
                    </div>
                    {b.comment && <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">📝 {b.comment}</div>}
                    <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                      <span>🕒 {t('Signalée', 'تم الإبلاغ')}: {b.reportedAt ? new Date(b.reportedAt).toLocaleString(isAr ? 'ar-MA' : 'fr-FR') : '—'}</span>
                      <span>⏱️ {isOpen ? openDuration(b) : formatDuration(b.durationMin, lang)}</span>
                      {(b.lat != null && b.lng != null) && (
                        <a href={`https://maps.google.com/?q=${b.lat},${b.lng}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">📍 {t('Position', 'الموقع')}</a>
                      )}
                      {!isOpen && b.closedAt && <span>✅ {new Date(b.closedAt).toLocaleString(isAr ? 'ar-MA' : 'fr-FR')}</span>}
                    </div>
                  </div>
                  {isOpen && (
                    <button onClick={() => { setClosing(b); setCloseComment(''); }}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold text-xs whitespace-nowrap">
                      ✅ {t('Clôturer', 'إغلاق')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Report modal */}
      {showReport && (
        <div className="fixed inset-0 z-[2000] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowReport(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} dir={isAr ? 'rtl' : 'ltr'}>
            <h3 className="font-black text-gray-900 dark:text-white">🛠️ {t('Signaler une panne', 'الإبلاغ عن عطل')}</h3>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">{t('Véhicule', 'المركبة')}</label>
              <select value={formCarId} onChange={e => setFormCarId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm">
                <option value="">{t('— Choisir —', '— اختر —')}</option>
                {reportableCars.map(c => <option key={c.id} value={c.id}>{carLabelOf(c)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">{t('Gravité', 'الخطورة')}</label>
              <div className="flex gap-2">
                {SEVERITIES.map(s => (
                  <button key={s.key} onClick={() => setFormSeverity(s.key)}
                    className={`flex-1 px-2 py-1.5 rounded-lg text-sm font-bold ${formSeverity === s.key ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                    {t(s.fr, s.ar)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1">{t('Description (optionnel)', 'الوصف (اختياري)')}</label>
              <textarea value={formComment} onChange={e => setFormComment(e.target.value)} rows={3}
                placeholder={t('Décrivez le problème…', 'صف المشكلة…')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm" />
            </div>
            <p className="text-[11px] text-gray-400">
              {t('Le véhicule passera en « En réparation » et l\'administrateur recevra une alerte Telegram (nom, position GPS, date/heure, commentaire).',
                 'ستصبح المركبة « قيد الإصلاح » وسيتلقى المدير تنبيهاً عبر Telegram (الاسم، الموقع، التاريخ، التعليق).')}
            </p>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowReport(false)} className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl font-bold text-sm">{t('Annuler', 'إلغاء')}</button>
              <button onClick={submitReport} disabled={busy} className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-sm disabled:opacity-60">
                {busy ? '⏳' : t('Signaler', 'إبلاغ')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close modal */}
      {closing && (
        <div className="fixed inset-0 z-[2000] bg-black/50 flex items-center justify-center p-4" onClick={() => setClosing(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} dir={isAr ? 'rtl' : 'ltr'}>
            <h3 className="font-black text-gray-900 dark:text-white">✅ {t('Clôturer la réparation', 'إغلاق الإصلاح')}</h3>
            <div className="text-sm text-gray-600 dark:text-gray-300">🚗 {closing.carLabel}</div>
            <div className="text-xs text-gray-400">⏱️ {t('Durée', 'المدة')}: {openDuration(closing)}</div>
            <textarea value={closeComment} onChange={e => setCloseComment(e.target.value)} rows={3}
              placeholder={t('Note de réparation (optionnel)…', 'ملاحظة الإصلاح (اختياري)…')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm" />
            <p className="text-[11px] text-gray-400">{t('Le véhicule reviendra à son statut normal.', 'ستعود المركبة إلى حالتها العادية.')}</p>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setClosing(null)} className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl font-bold text-sm">{t('Annuler', 'إلغاء')}</button>
              <button onClick={submitClose} disabled={busy} className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-sm disabled:opacity-60">
                {busy ? '⏳' : t('Clôturer', 'إغلاق')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[2100] bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
